/**
 * Portal Detection Utility
 * Detects which portal (admin vs owner) based on hostname
 * Provides guardrails to prevent cross-portal API calls
 */

/**
 * Detect portal type from hostname
 * @returns {'admin'|'owner'|'unknown'}
 */
function getPortalOverride() {
  if (typeof window === 'undefined') return null;

  const override = new URLSearchParams(window.location.search).get('portal');
  return override === 'admin' || override === 'owner' ? override : null;
}

function isLocalHostname(hostname) {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0';
}

function detectLocalPortal(pathname) {
  if (pathname.startsWith('/admin') || pathname.startsWith('/modules')) {
    return 'admin';
  }

  if (pathname.startsWith('/owner')) {
    return 'owner';
  }

  // The local Vite app in this repo is the admin shell unless explicitly overridden.
  return 'admin';
}

export function detectPortal() {
  if (typeof window === 'undefined') return 'unknown';

  const hostname = window.location.hostname.toLowerCase();
  const pathname = window.location.pathname.toLowerCase();
  const override = getPortalOverride();

  if (override) {
    return override;
  }

  if (hostname.includes('admin.') || hostname.startsWith('admin')) {
    return 'admin';
  }

  if (hostname.includes('owner') || hostname.includes('clcvacation')) {
    return 'owner';
  }

  if (isLocalHostname(hostname)) {
    return detectLocalPortal(pathname);
  }

  // Default to owner for backward compatibility
  return 'owner';
}

/**
 * Get API base path based on portal
 * @returns {string} '/api/v1/admin' or '/api/v1/owner'
 */
export function getApiBase() {
  const portal = detectPortal();
  return portal === 'admin' ? '/api/v1/admin' : '/api/v1/owner';
}

/**
 * Guardrail: Warn if admin portal tries to call owner endpoints
 * @param {string} url - The URL being called
 * @returns {boolean} true if call should proceed, false if blocked
 */
export function checkPortalApiGuard(url) {
  const portal = detectPortal();
  
  if (portal === 'admin' && url.includes('/api/v1/owner/')) {
    const error = new Error(
      `[ADMIN PORTAL GUARD] Blocked attempt to call owner endpoint: ${url}. ` +
      `Admin portal must only use /api/v1/admin/* endpoints.`
    );
    console.error(error.message);
    console.error('[ADMIN PORTAL GUARD] Stack trace:', new Error().stack);
    // Don't throw - just warn for now to avoid breaking existing code
    // In future, we may want to throw here
    return true; // Allow for now, but log warning
  }
  
  if (portal === 'owner' && url.includes('/api/v1/admin/')) {
    const error = new Error(
      `[OWNER PORTAL GUARD] Blocked attempt to call admin endpoint: ${url}. ` +
      `Owner portal must only use /api/v1/owner/* endpoints.`
    );
    console.error(error.message);
    throw error; // Owner portal guard is stricter
  }
  
  return true;
}

/**
 * Log portal detection at startup
 */
export function logPortalDetection() {
  if (typeof window === 'undefined') return;
  
  const portal = detectPortal();
  const apiBase = getApiBase();
  const hostname = window.location.hostname;
  
  console.log('[PORTAL DETECTION]', {
    portal,
    hostname,
    apiBase,
    isAdmin: portal === 'admin',
    timestamp: new Date().toISOString(),
  });
}
